import { AviosCurrencyBadge, Box, Heading, Paragraph } from '@alto-avios/alto-ui'
import { ContentfulCardLegacy } from '@reward-platform/contentful'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { trackCtaClick } from '~/components/search-and-results/Card/MediaCard.utils'
import useGetAirport from '~/hooks/useGetAirport/useGetAirport'
import { SearchTabQuery } from '~/schemas/searchPageParams'
import { getContentfulImageProps } from '~/utils/assets'
import { normalizeUrl } from '~/utils/url'
import Link from 'next/link'
import style from './InteractivePod.module.scss'

type Props = {
  card: ContentfulCardLegacy
  product: SearchTabQuery
}

export function InteractivePod({
  card: { image, bynderImage, heading, subHeading, link, caption, destinationAirport, avios },
  product,
}: Props) {
  const t = useTranslations('pages.flights.interactive_pods')
  const router = useRouter()

  const [isHovering, setIsHovering] = useState(false)
  useGetAirport(isHovering ? destinationAirport : undefined)
  const { imgSrc, imgAlt } = getContentfulImageProps({
    bynderImage,
    image,
    fallbackAlt: heading,
  })

  const content = (
    <div className={style.pod}>
      {imgSrc ? (
        <Media imageUrl={normalizeUrl(imgSrc)} imageAlt={imgAlt} ribbonText={caption} />
      ) : null}

      <Box flexDirection="column" gap="sm" alignItems="center" padding="sm">
        <Box flexDirection="column" gap="3xs" alignItems="center">
          <Heading size="xxs" textAlign="center">
            {heading}
          </Heading>

          {avios ? (
            <AviosCurrencyBadge
              kind="spend"
              textStart={t('from')}
              textEnd={t('taxes_and_fees')}
              size="md"
              width="full"
            >
              {avios}
            </AviosCurrencyBadge>
          ) : null}
        </Box>

        <Box maxWidth={{ sm: '90%', lg: 'unset' }}>
          <Paragraph size="sm" textAlign="center">
            {subHeading}
          </Paragraph>
        </Box>
      </Box>

      <Cta />
    </div>
  )

  if (link?.length) {
    return (
      <Link href={link} prefetch={false} data-testid="interactive-pod" className={style.link}>
        {content}
      </Link>
    )
  }
  if (product === 'flights' && destinationAirport) {
    return (
      <Link
        data-testid="interactive-pod"
        onClick={() => trackCtaClick(destinationAirport)}
        prefetch={false}
        shallow
        title={heading}
        onPointerOver={() => setIsHovering(true)}
        className={style.link}
        href={{
          pathname: router.pathname,
          query: {
            tab: 'flights',
            toAirportCode: destinationAirport,
            oneWayOnly: false,
          },
        }}
      >
        {content}
      </Link>
    )
  }
  return content
}

function Cta() {
  const t = useTranslations('pages.flights.interactive_pods')
  return (
    <div className={style.cta}>
      <Paragraph textAlign="center" size="sm" foregroundColor="accentPrimary">
        {t('cta')}
      </Paragraph>
    </div>
  )
}

type MediaProps = {
  imageUrl: string
  imageAlt: string
  ribbonText?: string
}

function Media({ imageUrl, imageAlt, ribbonText }: MediaProps) {
  return (
    <div className={style.media}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={imageUrl}
        alt={imageAlt}
        width="100%"
        height="100%"
        loading="lazy"
        className={style.media_image}
      />

      {!!ribbonText?.length && (
        <div className={style.media_ribbon}>
          <Paragraph textAlign="center" foregroundColor="inverseOnSubtle" size="sm">
            {ribbonText}
          </Paragraph>
        </div>
      )}
    </div>
  )
}
